import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { logNav } from "@/lib/navigationLogger";
import { isNative } from "@/lib/capacitor";
import { scrollContainersToTop } from "@/lib/scrollWizardToTop";

/**
 * Scroll restoration for the SPA.
 *
 * - PUSH / REPLACE navigations start the new page at the top (traditional website behaviour).
 * - POP navigations (browser Back / Forward) restore the previous scroll position.
 * - Same-page anchor links (#section) are honoured and never overridden.
 *
 * Also logs navigation events for debugging native routing issues.
 */

// Scroll offsets keyed by history entry key, so Back/Forward can restore them.
const scrollPositions = new Map<string, number>();

// Mirror the offsets into sessionStorage so a cross-document Back/Forward (full reload,
// e.g. returning from an external link) can still restore the previous position.
const STORE_KEY = "tm:scrollPositions";

const readStoredOffset = (key: string): number | undefined => {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Record<string, number>;
    const value = parsed?.[key];
    return typeof value === "number" ? value : undefined;
  } catch {
    return undefined;
  }
};

const writeStoredOffset = (key: string, offset: number) => {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    parsed[key] = offset;
    // Keep the store small — only the most recent entries matter.
    const entries = Object.entries(parsed).slice(-30);
    sessionStorage.setItem(STORE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    // Storage unavailable (private mode / quota) — in-memory map still works.
  }
};

export const ScrollToTopOnNavigate = () => {
  const location = useLocation();
  const { pathname, hash, key } = location;
  const navigationType = useNavigationType();
  const prevPathRef = useRef(pathname);
  const activeKeyRef = useRef(key);

  // Take over scroll restoration from the browser (it cannot do it correctly for SPAs).
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      const previous = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => {
        window.history.scrollRestoration = previous;
      };
    }
  }, []);

  // Continuously record the scroll offset of the current history entry (rAF-throttled).
  useEffect(() => {
    activeKeyRef.current = key;
    let frame = 0;
    const record = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        scrollPositions.set(activeKeyRef.current, window.scrollY);
        writeStoredOffset(activeKeyRef.current, window.scrollY);
      });
    };
    window.addEventListener("scroll", record, { passive: true });
    return () => {
      // Deliberately do NOT re-record here: by cleanup time the outgoing page's DOM
      // has been replaced, so the browser may already have clamped the offset.
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", record);
    };
  }, [key]);

  useEffect(() => {
    if (prevPathRef.current !== pathname) {
      logNav(prevPathRef.current, pathname);
      prevPathRef.current = pathname;
    }

    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const restoredOffset =
      navigationType === "POP" ? scrollPositions.get(key) ?? readStoredOffset(key) : undefined;

    // Once the visitor touches the page, they own the scroll position — every pending
    // deferred pass is abandoned. Without this, late passes yank the page back to the
    // top mid-gesture, which reads as scrolling "locking up" or jumping.
    let userTookOver = false;
    const timers: number[] = [];
    let raf = 0;
    const release = () => {
      if (userTookOver) return;
      userTookOver = true;
      if (raf) cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };

    // Set once the requested #anchor has actually been reached, so later passes stop.
    let anchorResolved = false;

    const scrollToDestination = () => {
      if (userTookOver) return;

      // Anchor links always win — never fight a #section target.
      if (hash) {
        if (anchorResolved) return;
        const target = document.getElementById(decodeURIComponent(hash.slice(1)));
        if (target) {
          anchorResolved = true;
          target.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" });
        }
        // Target not mounted yet (lazy route still suspended) — wait for a later pass
        // instead of snapping to the top and losing the anchor.
        return;
      }

      const top = restoredOffset ?? 0;
      // The native shell (and some layouts) scroll a container rather than the
      // window, so a window-only reset leaves the new screen halfway down.
      if (top === 0) scrollContainersToTop("auto");
      if (Math.abs(window.scrollY - top) < 2) return;
      // Always instant for route changes: an animated programmatic scroll competes with
      // the visitor's wheel/trackpad input in Chrome and Safari.
      window.scrollTo({ top, behavior: "auto" });
    };

    // Immediate scroll
    scrollToDestination();
    // Beat lazy-route / lazy-image layout timing with a couple of deferred passes.
    // Restoring a Back/Forward position needs a slightly longer window because the page
    // can still be growing (suspense boundaries, images) when the route first mounts.
    raf = requestAnimationFrame(scrollToDestination);
    // Lazy marketing routes can take well over 300ms to mount, so anchor targets get a
    // longer retry window than plain scroll restoration.
    const delays = hash
      ? [50, 150, 300, 600, 1000, 1500, 2000]
      : restoredOffset !== undefined
        ? [50, 150, 300]
        : [50, 150];
    delays.forEach((d) => timers.push(window.setTimeout(scrollToDestination, d)));
    if (hash) {
      // Safety net: an anchor that never appears must not leave the page mid-scroll.
      timers.push(
        window.setTimeout(() => {
          if (userTookOver || anchorResolved) return;
          window.scrollTo({ top: 0, behavior: "auto" });
        }, 2200),
      );
    }


    const opts = { passive: true, capture: true } as AddEventListenerOptions;
    window.addEventListener("wheel", release, opts);
    window.addEventListener("touchstart", release, opts);
    window.addEventListener("pointerdown", release, opts);
    window.addEventListener("keydown", release, opts);

    return () => {
      release();
      window.removeEventListener("wheel", release, opts);
      window.removeEventListener("touchstart", release, opts);
      window.removeEventListener("pointerdown", release, opts);
      window.removeEventListener("keydown", release, opts);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, hash, key, navigationType]);


  return null;
};
